import React, { Component } from 'react';
import Inherit from '../../core/inherit';
import LoginController from './login.controller';
import withNavigate from '../../core/withNavigate';
import { Link } from 'react-router-dom';

import { Button, Input, Tooltip } from 'antd';
import { Mail, Lock, Eye, EyeOff, LogIn, LifeBuoy, ArrowRight } from 'lucide-react';
import { AuthLayout } from '../common';
import { AuthBrandMark } from '../common/layout/AuthLayout';
import { isCentralConfigured } from '../../data/central';

const SUBTITLES = {
    email: 'Ingresa tu correo para continuar',
    password: 'Ingresa tu contraseña para acceder al sistema de gestión Shelf',
    classic: 'Ingresa tus credenciales para acceder al sistema de gestión Shelf'
};

class ShelfLogin extends Inherit(LoginController, Component) {
    state = {
        // 'email' → look the email up in central-service; 'password' → sign in here.
        // Without REACT_APP_CENTRAL_URL there is no lookup: start at 'password' (classic form).
        step: isCentralConfigured ? 'email' : 'password',
        email: '',
        password: '',
        disabled: false,
        redirectingTo: null,
        // The company resolved for the email (company/session.js shape); null until resolved.
        company: null,
        passwordVisibility: 'password',
    };

    renderEmailInput() {
        const { email, disabled } = this.state;
        return (
            <div>
                <label htmlFor="login-email" className="mb-1.5 block text-sm font-medium text-surface-700">
                    Correo electrónico
                </label>
                <Input
                    id="login-email"
                    size="large"
                    type="email"
                    autoComplete="username"
                    autoFocus
                    value={email}
                    disabled={disabled}
                    onChange={(e) => this.setEmail(e.target.value)}
                    placeholder="tu@empresa.com"
                    prefix={<Mail className="text-surface-400" />}
                    allowClear
                />
            </div>
        );
    }

    /** The email chosen in step 1, with a way back to change it. */
    renderEmailSummary() {
        const { email, disabled } = this.state;
        return (
            <div className="flex items-center gap-3 rounded-lg border border-surface-200 bg-surface-50 py-2 pl-3 pr-1.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-[15px] text-primary-600">
                    <Mail />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="text-xs text-surface-500">Correo electrónico</div>
                    <div className="truncate text-sm font-medium text-surface-900" title={email}>
                        {email}
                    </div>
                </div>
                <Button type="link" size="small" disabled={disabled} onClick={() => this.changeEmail()}>
                    Cambiar
                </Button>
                {/* Lets password managers pair the saved password with this email. */}
                <input type="email" name="username" autoComplete="username" value={email} readOnly tabIndex={-1} aria-hidden="true" className="sr-only" />
            </div>
        );
    }

    renderPasswordInput() {
        const { password, disabled, passwordVisibility } = this.state;
        const showingPassword = passwordVisibility === 'text';
        return (
            <div>
                <label htmlFor="login-password" className="mb-1.5 block text-sm font-medium text-surface-700">
                    Contraseña
                </label>
                <Input
                    id="login-password"
                    size="large"
                    type={passwordVisibility}
                    autoComplete="current-password"
                    autoFocus={isCentralConfigured}
                    value={password}
                    disabled={disabled}
                    placeholder="••••••••"
                    onChange={(e) => this.setPassword(e.target.value)}
                    prefix={<Lock className="text-surface-400" />}
                    suffix={
                        <Tooltip title={showingPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
                            <button
                                type="button"
                                aria-label={showingPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                className="-mr-1 flex h-7 w-7 items-center justify-center rounded-md border-0 bg-transparent text-[16px] text-surface-400 cursor-pointer transition-colors hover:bg-surface-100 hover:text-surface-600"
                                onClick={() => this.togglePasswordVisibility()}>
                                {showingPassword ? <Eye /> : <EyeOff />}
                            </button>
                        </Tooltip>
                    }
                />
                <div className="mt-2 flex justify-end">
                    <Link
                        to="/forgot-password"
                        state={this.state.email.trim() ? { email: this.state.email.trim() } : undefined}
                        className="text-sm font-medium text-primary-600 transition-colors hover:text-primary-700">
                        ¿Olvidaste tu contraseña?
                    </Link>
                </div>
            </div>
        );
    }

    /** The sign-in card. `embedded` renders it bare (landing page hero) instead of inside AuthLayout. */
    renderCard() {
        const { step, disabled, redirectingTo } = this.state;
        const { embedded } = this.props;
        const emailStep = step === 'email';
        const subtitle = emailStep ? SUBTITLES.email : isCentralConfigured ? SUBTITLES.password : SUBTITLES.classic;

        return (
                <div className={embedded ? 'w-full' : 'w-full max-w-[400px]'}>
                    {this.props.contextHolder}

                    <div className={embedded ? 'lp-login-card p-6 sm:p-7' : 'shelf-card p-6 sm:p-8'}>
                        {/* Brand + heading */}
                        <div className={embedded ? 'mb-5' : 'mb-7'}>
                            {!embedded && <AuthBrandMark size="lg" />}
                            <h1 className={`m-0 font-semibold tracking-tight text-surface-900 ${embedded ? 'text-xl' : 'mt-5 text-2xl'}`}>
                                {embedded ? 'Accede a tu panel' : 'Panel Administrativo'}
                            </h1>
                            <p className="m-0 mt-1.5 text-sm text-surface-500">{subtitle}</p>
                        </div>

                        {/* Form: the submit button and Enter in any field continue / sign in */}
                        <form noValidate onSubmit={(e) => this.submit(e)} className="space-y-4">
                            {emailStep || !isCentralConfigured ? this.renderEmailInput() : this.renderEmailSummary()}
                            {!emailStep && this.renderPasswordInput()}

                            {emailStep ? (
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    size="large"
                                    block
                                    loading={disabled}
                                    disabled={disabled}
                                    icon={<ArrowRight />}
                                    iconPosition="end"
                                    className="!mt-6">
                                    {redirectingTo ? `Redirigiendo a ${redirectingTo}…` : 'Continuar'}
                                </Button>
                            ) : (
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    size="large"
                                    block
                                    loading={disabled}
                                    disabled={disabled}
                                    icon={<LogIn />}
                                    iconPosition="end"
                                    className="!mt-6">
                                    Iniciar Sesión
                                </Button>
                            )}
                        </form>

                        {!embedded && (
                            <div className="mt-7 pt-5 border-t border-surface-100 text-center">
                                <p className="m-0 text-surface-400 text-xs">© {new Date().getFullYear()} Shelf. Todos los derechos reservados.</p>
                            </div>
                        )}
                    </div>

                    {/* Support */}
                    <div className={`flex items-center gap-1.5 text-sm text-surface-500 ${embedded ? 'mt-4 justify-start' : 'mt-5 justify-center'}`}>
                        <LifeBuoy className="text-surface-400" />
                        <span>¿Necesitas ayuda?</span>
                        <a href="/support" className="font-medium text-primary-600 hover:text-primary-700 transition-colors">
                            Contáctanos
                        </a>
                    </div>
                </div>
        );
    }

    render() {
        if (this.props.embedded) return this.renderCard();
        return <AuthLayout>{this.renderCard()}</AuthLayout>;
    }
}

export default withNavigate(ShelfLogin);
